import { useRef, useState } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Settings2, X, Plus, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useNavigate } from "@/lib/useNav";
import { Card } from "@/components/shared/UI";
import { BLOCKS, WIDTHS, WIDTH_LABEL, HEIGHTS, HEIGHT_LABEL } from "@/lib/dashboardBlocks";
import { BLOCK_COMPONENTS, BLOCK_PAGE } from "@/components/dashboard/blockRegistry";
import { usePermissions } from "@/context/PermissionsContext";

const GAP = 20;
const nearestW = (cols) => WIDTHS.reduce((a, b) => (Math.abs(b - cols) < Math.abs(a - cols) ? b : a));
const nearestH = (px) => Object.keys(HEIGHTS).reduce((a, b) => (Math.abs(HEIGHTS[b] - px) < Math.abs(HEIGHTS[a] - px) ? b : a));

function useResize(block, gridRef, cellRef, autoHeight, onUpdate) {
  const [preview, setPreview] = useState(null);
  const start = (axis) => (e) => {
    e.preventDefault(); e.stopPropagation();
    const colW = (gridRef.current.clientWidth + GAP) / 12;
    const rect = cellRef.current.getBoundingClientRect();
    const sx = e.clientX, sy = e.clientY;
    let cur = { w: block.w, h: block.h };
    const move = (ev) => {
      cur = { ...cur };
      if (axis.includes("x")) cur.w = nearestW((rect.width + ev.clientX - sx + GAP) / colW);
      if (axis.includes("y") && !autoHeight) cur.h = nearestH(rect.height + ev.clientY - sy);
      setPreview(cur);
    };
    const up = () => {
      window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up);
      setPreview(null);
      if (cur.w !== block.w || cur.h !== block.h) onUpdate(cur);
    };
    window.addEventListener("pointermove", move); window.addEventListener("pointerup", up);
  };
  return [preview, start];
}

function EditBar({ id, listeners, attributes, onSettings, onRemove }) {
  return (
    <div className="flex items-center gap-0.5">
      <button onClick={onSettings} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" data-testid={`block-${id}-settings-btn`} title="Block settings"><Settings2 className="h-4 w-4" /></button>
      <button onClick={onRemove} className="rounded-md p-1 text-slate-400 hover:bg-rose-50 hover:text-rose-600" data-testid={`block-${id}-remove-btn`} title="Remove block"><X className="h-4 w-4" /></button>
      <button {...listeners} {...attributes} className="cursor-grab touch-none rounded-md p-1 text-slate-400 hover:bg-slate-100 active:cursor-grabbing" data-testid={`block-${id}-drag-handle`} title="Drag to move"><GripVertical className="h-4 w-4" /></button>
    </div>
  );
}

export function BlockFrame({ block, editing, gridRef, onUpdate, onRemove, onSettings }) {
  const meta = BLOCKS[block.type];
  const Comp = BLOCK_COMPONENTS[block.type];
  const navigate = useNavigate();
  const cellRef = useRef(null);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: block.id, disabled: !editing });
  const [preview, startResize] = useResize(block, gridRef, cellRef, meta?.autoHeight, onUpdate);
  const { can } = usePermissions();
  if (!meta || !Comp) return null;
  if (BLOCK_PAGE[block.type] && !can(BLOCK_PAGE[block.type], "view")) return null;
  const w = preview?.w ?? block.w, h = preview?.h ?? block.h;
  const href = typeof meta.href === "function" ? meta.href(block.settings || {}) : meta.href;
  const Icon = meta.icon;
  const setRefs = (n) => { setNodeRef(n); cellRef.current = n; };
  return (
    <div ref={setRefs} className={cn("dash-cell relative focus-within:z-30", isDragging && "z-20 opacity-80")} data-testid={`dashboard-block-${block.id}`} data-block-type={block.type}
      style={{ "--span": w, "--span-md": w <= 6 ? 6 : 12, transform: CSS.Translate.toString(transform), transition }}>
      <Card className={cn("flex flex-col p-0 transition-shadow", !meta.autoHeight && "overflow-hidden", editing && "ring-2 ring-dashed ring-sky-200", preview && "ring-sky-400 shadow-lg")}
        style={{ height: meta.autoHeight ? undefined : HEIGHTS[h] }}>
        <div className="flex items-center gap-2 px-5 pb-2 pt-4">
          <Icon className="h-4 w-4 shrink-0 text-sky-600" />
          <h3 className="min-w-0 flex-1 truncate font-heading text-sm font-semibold text-slate-900" data-testid={`block-${block.id}-title`}>{block.title || meta.label}</h3>
          {editing ? <EditBar id={block.id} listeners={listeners} attributes={attributes} onSettings={onSettings} onRemove={onRemove} /> : (
            <div className="flex items-center gap-2">
              {meta.addHref && <button onClick={() => navigate(meta.addHref)} className="rounded-md p-1 text-sky-600 hover:bg-sky-50" title="Add" data-testid={`block-${block.id}-add-btn`}><Plus className="h-4 w-4" /></button>}
              {href && <button onClick={() => navigate(href)} className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-sky-600" title="Open" data-testid={`block-${block.id}-open-btn`}><ArrowUpRight className="h-4 w-4" /></button>}
            </div>
          )}
        </div>
        <div className={cn("min-h-0 flex-1 px-5 pb-4", !meta.autoHeight && "overflow-auto", editing && "pointer-events-none select-none")}>
          <Comp block={block} editing={editing} />
        </div>
      </Card>
      {editing && (
        <>
          {preview && <div className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className="rounded-full bg-sky-600 px-3 py-1 text-xs font-bold text-white shadow" data-testid={`block-${block.id}-size-preview`}>{WIDTH_LABEL[w]}{!meta.autoHeight && ` · ${HEIGHT_LABEL[h]}`}</span></div>}
          <div onPointerDown={startResize("x")} className="absolute -right-2 top-1/2 h-12 w-3 -translate-y-1/2 cursor-ew-resize touch-none rounded-full bg-sky-400/70 hover:bg-sky-500" data-testid={`block-${block.id}-resize-x`} title="Drag to resize width" />
          {!meta.autoHeight && <div onPointerDown={startResize("y")} className="absolute -bottom-2 left-1/2 h-3 w-12 -translate-x-1/2 cursor-ns-resize touch-none rounded-full bg-sky-400/70 hover:bg-sky-500" data-testid={`block-${block.id}-resize-y`} title="Drag to resize height" />}
          <div onPointerDown={startResize("xy")} className="absolute -bottom-1.5 -right-1.5 h-4 w-4 cursor-nwse-resize touch-none rounded-sm border-2 border-white bg-sky-500" data-testid={`block-${block.id}-resize-xy`} title="Drag to resize" />
        </>
      )}
    </div>
  );
}
